import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { Plus, Minus, Trash2, ShoppingBasket } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CartDrawer = () => {
  const { t } = useLang();
  const { items, subtotal, deliveryFee, amountToFreeTier, restaurant, updateQty, removeItem, drawerOpen, setDrawerOpen } = useCart();
  const navigate = useNavigate();

  const total = subtotal + deliveryFee;
  const minOrderNotMet = restaurant && subtotal < (restaurant.min_order || 0);

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-5 py-4 border-b border-navy/10">
          <SheetTitle className="font-heading text-navy">{t("yourBasket")}</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-3" data-testid="empty-basket">
            <div className="w-16 h-16 rounded-full bg-sand-100 flex items-center justify-center">
              <ShoppingBasket className="w-8 h-8 text-navy/40" />
            </div>
            <p className="text-navy/70">{t("emptyBasket")}</p>
            <Button
              onClick={() => { setDrawerOpen(false); navigate("/restaurants"); }}
              className="btn-coral mt-2"
              data-testid="basket-browse-btn"
            >
              {t("startOrdering")}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {restaurant && <div className="text-xs uppercase tracking-wider text-navy/60 mb-2">{restaurant.name}</div>}
              {items.map((it) => (
                <div key={it.key} className="flex gap-3 p-3 rounded-xl bg-sand-50 border border-navy/8" data-testid={`basket-item-${it.item_id}`}>
                  {it.image && <img src={it.image} alt={it.name} className="w-14 h-14 rounded-lg object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-navy text-sm truncate">{it.name}</div>
                    {it.addons.length > 0 && (
                      <div className="text-[10px] text-teal">+ {it.addons.map(a => a.name).join(", ")}</div>
                    )}
                    {it.removed_ingredients && it.removed_ingredients.length > 0 && (
                      <div className="text-[10px] text-coral">{t("without")}: {it.removed_ingredients.join(", ")}</div>
                    )}
                    <div className="text-sm text-navy/70 mt-1">€{it.price.toFixed(2)}</div>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button onClick={() => removeItem(it.key)} className="text-navy/40 hover:text-coral" data-testid={`remove-${it.item_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-1 bg-white rounded-full p-0.5 border border-navy/10">
                      <button onClick={() => updateQty(it.key, it.qty - 1)} className="w-6 h-6 flex items-center justify-center">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-xs font-semibold w-5 text-center">{it.qty}</span>
                      <button onClick={() => updateQty(it.key, it.qty + 1)} className="w-6 h-6 flex items-center justify-center">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-navy/10 p-5 space-y-2 bg-white">
              <div className="flex justify-between text-sm text-navy/70">
                <span>{t("subtotal")}</span>
                <span data-testid="basket-subtotal">€{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-navy/70">
                <span>{t("deliveryFee")}</span>
                <span>€{deliveryFee.toFixed(2)}</span>
              </div>
              {amountToFreeTier > 0 && subtotal > 0 && (
                <div className="text-[11px] text-teal bg-teal/10 rounded-lg px-2 py-1.5 leading-tight" data-testid="delivery-hint">
                  {t("deliveryHintAdd")} <b>€{amountToFreeTier.toFixed(2)}</b> {t("deliveryHintFor")} <b>€2.99</b> {t("deliveryHintDelivery")}
                </div>
              )}
              {amountToFreeTier === 0 && subtotal > 0 && (
                <div className="text-[11px] text-teal bg-teal/10 rounded-lg px-2 py-1.5" data-testid="delivery-hint-reached">
                  ✓ {t("deliveryHintUnlocked")}
                </div>
              )}
              <div className="flex justify-between font-bold text-navy text-lg pt-2 border-t border-navy/10">
                <span>{t("total")}</span>
                <span data-testid="basket-total">€{total.toFixed(2)}</span>
              </div>
              {minOrderNotMet && (
                <div className="text-xs text-coral">Min. order: €{restaurant.min_order.toFixed(2)}</div>
              )}
              <Button
                onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}
                className="btn-coral w-full mt-2"
                disabled={minOrderNotMet}
                data-testid="proceed-checkout-btn"
              >
                {t("proceedCheckout")}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
